import { Check, Info, Rocket, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useState } from 'react'
import { toast } from '@/hooks/use-toast'
import { createCheckoutSession } from '@/api/billing'
import PlanFormEmail from '@/features/settings/usage/plan-form-email'
import { usePlan } from '@/hooks/use-plan'
import { Spinner } from '@/components/ui/spinner'
import { useTranslation } from 'react-i18next'
import { tokens } from '@/locales/translations/tokens'
import { useFormmatedPlanName } from '@/hooks/use-formmated-plan-name'

interface OrganizationPricingProps {
  className?: string
}

const PLAN_RANKING = {
  'community': 1,
  'commercial': 2,
  'enterprise': 3
}

export default function OrganizationPricing({ className }: OrganizationPricingProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false)
  const { plan } = usePlan()
  const currentPlanType = plan?.type || null
  const formattedPlanName = useFormmatedPlanName(currentPlanType || "community");
  const CURRENCY = "CA$";
  
  const isPlanDisabled = (planType: string): boolean => {
    if (!currentPlanType) return false
    
    return PLAN_RANKING[planType as keyof typeof PLAN_RANKING] < PLAN_RANKING[currentPlanType as keyof typeof PLAN_RANKING]
  }
  
  const handleSubscribe = async (planType: string) => {
    if(planType === 'community') {
      window.location.replace('/')
      setIsLoading(true)
      return
    }
    try {
      setIsLoading(true)

      const response = await createCheckoutSession({type: planType})
      if (response.url) {
        window.location.href = response.url
      }
    } catch (_err) {
      toast({
        variant: 'destructive',
        title: t(tokens.ui.toasts.error.title),
        description: t(tokens.ui.toasts.subscription.failed)
      })
    } finally {
      setIsLoading(false)
    }
  }
  
  const FeatureItem = ({ included, text, tooltip }: { included: boolean; text: string; tooltip?: string }) => (
    <div className="flex items-center space-x-2">
      {included ? (
        <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
      ) : (
        <X className="h-4 w-4 text-gray-400 flex-shrink-0" />
      )}
      <span className="text-sm break-normal">{text}</span>
      {tooltip && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0" />
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs max-w-xs">{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  )

  return (
    <div className={`space-y-8 ${className}`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-y-8 gap-x-6 md:gap-8">
        <Card className="relative flex flex-col border-border/40 h-full">
          <CardHeader>
            <CardTitle className="text-xl">{t(tokens.plans.community)}</CardTitle>
            <CardDescription className="text-sm">
              {t(tokens.onboarding.organization_pricing.plans.description.individual_users)}
            </CardDescription>
            <div className="mt-4">
              <span className="text-2xl font-bold">{CURRENCY}0</span>
              <span className="text-muted-foreground ml-1">/{t(tokens.onboarding.organization_pricing.month)}</span>
            </div>
          </CardHeader>
          <CardContent className="flex-grow space-y-4">
            <div className="space-y-2">
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.questions, { count: 15 })} tooltip={t(tokens.onboarding.organization_pricing.tooltips.questions)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.max_mb_docs, { count: 10 })} tooltip={t(tokens.onboarding.organization_pricing.tooltips.max_mb_docs)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.max_mb_spreadsheets, { count: 5 })} tooltip={t(tokens.onboarding.organization_pricing.tooltips.max_mb_spreadsheets)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.max_mb_translator, { count: 10 })} tooltip={t(tokens.onboarding.organization_pricing.tooltips.max_mb_translator)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.minutes, { count: 30 })} tooltip={t(tokens.onboarding.organization_pricing.tooltips.minutes)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.users, { count: 1 })} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.low_priority)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.slack_support)} />
              <FeatureItem included={false} text={t(tokens.onboarding.organization_pricing.features.multi_provider)} tooltip={t(tokens.onboarding.organization_pricing.tooltips.multi_provider)} />

            </div>
          </CardContent>
          <CardFooter>
            <Button 
              className="w-full" 
              variant="outline"
              onClick={() => handleSubscribe('community')}
              disabled={isLoading || isPlanDisabled('community')}
            >
              {isLoading ? (
                <Spinner size='22' />
              ) : (
                currentPlanType === 'community' ?
                t(tokens.onboarding.organization_pricing.continue_with_plan, {
                  currentPlan: formattedPlanName
                }) : 'Start Now'
              )}
            </Button>
          </CardFooter>
        </Card>

        <Card className="relative flex flex-col border-primary/60 shadow-md h-full">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
            {t(tokens.onboarding.organization_pricing.most_popular)}
          </div>
          <CardHeader>
            <CardTitle className="text-xl">{t(tokens.plans.commercial)}</CardTitle>
            <CardDescription className="text-sm">
              {t(tokens.onboarding.organization_pricing.plans.description.growing_teams)}
            </CardDescription>
            <div className="mt-4">
              <span className="text-2xl font-bold">
                {CURRENCY}100
              </span>
              <span className="text-muted-foreground ml-1">/{t(tokens.onboarding.organization_pricing.month)}</span>
            </div>
          </CardHeader>
          <CardContent className="flex-grow space-y-4">
            <div className="space-y-2">
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.questions, { count: 500 })} tooltip={t(tokens.onboarding.organization_pricing.tooltips.questions)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.max_mb_docs, { count: 100 })} tooltip={t(tokens.onboarding.organization_pricing.tooltips.max_mb_docs)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.max_mb_spreadsheets, { count: 50 })} tooltip={t(tokens.onboarding.organization_pricing.tooltips.max_mb_spreadsheets)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.max_mb_translator, { count: 100 })} tooltip={t(tokens.onboarding.organization_pricing.tooltips.max_mb_translator)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.minutes, { count: 1000 })} tooltip={t(tokens.onboarding.organization_pricing.tooltips.minutes)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.users, { count: 20 })} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.multi_provider)} tooltip={t(tokens.onboarding.organization_pricing.tooltips.multi_provider)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.dedicated_support)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.priority_queues)} />
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              className="w-full" 
              onClick={() => handleSubscribe('commercial')}
              disabled={isLoading || isPlanDisabled('commercial')}
            >
              {isLoading ? (
                <Spinner size='22' />
              ) : (
                currentPlanType === 'commercial' ? 
                t(tokens.onboarding.organization_pricing.continue_with_plan, {
                  currentPlan: formattedPlanName
                }) :
                <><Rocket /> {t(tokens.onboarding.organization_pricing.subscribe_now)}</>
              )}
            </Button>
          </CardFooter>
        </Card>

        <Card className="relative flex flex-col border-border/40 h-full">
          <CardHeader>
            <CardTitle className="text-xl">{t(tokens.plans.enterprise)}</CardTitle>
            <CardDescription className="text-sm">
              {t(tokens.onboarding.organization_pricing.plans.description.large_organizations)}
            </CardDescription>
            <div className="mt-4">
              <span className="text-xl font-bold">{t(tokens.onboarding.organization_pricing.custom)}</span>
            </div>
          </CardHeader>
          <CardContent className="flex-grow space-y-4">
            <div className="space-y-2">
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.high_question_limit)} tooltip={t(tokens.onboarding.organization_pricing.tooltips.high_question_limit)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.unlimited_pages)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.unlimited_minutes)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.unlimited_users)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.advanced_multi_provider)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.separate_tenant)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.sso)} tooltip={t(tokens.onboarding.organization_pricing.tooltips.sso)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.guaranteed_sla)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.security_report)} />
              <FeatureItem included={true} text={t(tokens.onboarding.organization_pricing.features.xlsx_import)} />
            </div>
          </CardContent>
          <CardFooter>
            {isLoading ? (
              <Spinner size='22' />
            ) : (
              currentPlanType === 'enterprise' ? (
                <Button 
                  className="w-full" 
                  onClick={() => window.location.replace('/')}
                >
                  {t(tokens.onboarding.organization_pricing.continue_with_plan, {
                    currentPlan: formattedPlanName
                  })}
                </Button>
              ) : (
                <PlanFormEmail className='w-full' />
              )
            )}
          </CardFooter>
        </Card>
      </div>
    </div>
  )
} 